// src/AuthContext.js
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import API_URL from './config/apiConfig';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [authToken, setAuthToken] = useState(null);
    const [loading, setLoading] = useState(true);

    // Anti-boucle : évite plusieurs refresh simultanés
    const isRefreshing = useRef(false);
    const refreshPromise = useRef(null);

    // ✅ 1. FONCTION LOGIN : On sauvegarde tout dans le navigateur
    const login = useCallback((token, userId, userName, userEmail, isAdmin) => {
        const userData = { token, userId, userName, userEmail, isAdmin };

        // Mise à jour du State (Mémoire vive)
        setAuthToken(userData);

        // Mise à jour du Navigateur (Disque dur)
        localStorage.setItem('auth-token', token);
        localStorage.setItem('auth-user', JSON.stringify(userData));

        // Configuration Axios
        axios.defaults.headers.common['auth-token'] = token;

        setLoading(false);
    }, []);

    // ✅ 2. FONCTION LOGOUT : On nettoie tout
    const logout = useCallback(() => {
        localStorage.removeItem('auth-token');
        localStorage.removeItem('auth-user');
        delete axios.defaults.headers.common['auth-token'];
        setAuthToken(null);
        setLoading(false);
    }, []);

    // ✅ 3. REFRESH TOKEN : Renouvelle le token silencieusement
    const refreshToken = useCallback(async () => {
        // Si un refresh est déjà en cours, on attend le même résultat
        if (isRefreshing.current && refreshPromise.current) {
            return refreshPromise.current;
        }

        const storedToken = localStorage.getItem('auth-token');
        if (!storedToken) {
            logout();
            return null;
        }

        isRefreshing.current = true;

        refreshPromise.current = (async () => {
            try {
                const response = await axios.post(`${API_URL}/auth/refresh`, {}, {
                    headers: { 'auth-token': storedToken },
                    _skipInterceptor: true // Flag pour ne pas boucler
                });

                const { token, userId, userName, userEmail, isAdmin } = response.data;
                login(token, userId, userName, userEmail, isAdmin);
                console.log("🔄 Token rafraîchi automatiquement");
                return token;
            } catch (error) {
                console.error("❌ Impossible de rafraîchir le token, déconnexion");
                logout();
                return null;
            } finally {
                isRefreshing.current = false;
                refreshPromise.current = null;
            }
        })();

        return refreshPromise.current;
    }, [login, logout]);

    // ✅ 4. INTERCEPTEUR AXIOS : Gestion automatique des 401
    useEffect(() => {
        const responseInterceptor = axios.interceptors.response.use(
            // Réponse OK : on laisse passer
            (response) => response,
            // Erreur : on intercepte les 401
            async (error) => {
                const originalRequest = error.config;

                // Si c'est un 401 ET que ce n'est pas déjà un retry ET pas le login/refresh
                if (
                    error.response?.status === 401 &&
                    !originalRequest._retry &&
                    !originalRequest._skipInterceptor &&
                    !originalRequest.url?.includes('/auth/login') &&
                    !originalRequest.url?.includes('/auth/refresh')
                ) {
                    originalRequest._retry = true;

                    const newToken = await refreshToken();
                    if (newToken) {
                        // On relance la requête originale avec le nouveau token
                        originalRequest.headers['auth-token'] = newToken;
                        return axios(originalRequest);
                    }
                }

                return Promise.reject(error);
            }
        );

        // Nettoyage à la destruction du composant
        return () => {
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, [refreshToken]);

    // ✅ 5. AU CHARGEMENT (F5) : On restaure et on valide le token
    useEffect(() => {
        const loadUserFromStorage = async () => {
            try {
                const storedToken = localStorage.getItem('auth-token');
                const storedUser = localStorage.getItem('auth-user');

                if (storedToken && storedUser) {
                    const parsedUser = JSON.parse(storedUser);

                    // Restauration immédiate (pas d'attente réseau)
                    setAuthToken(parsedUser);
                    axios.defaults.headers.common['auth-token'] = storedToken;
                    console.log("🔄 Session restaurée depuis le stockage local");

                    // Validation silencieuse en arrière-plan (vérifie que le token est encore valide)
                    try {
                        const response = await axios.get(`${API_URL}/auth/me`, {
                            headers: { 'auth-token': storedToken }
                        });

                        // Si le serveur répond, le token est valide
                        // On met à jour les données utilisateur avec les données fraîches du serveur
                        if (response.data) {
                            const freshUser = {
                                token: storedToken,
                                userId: response.data.id,
                                userName: response.data.name,
                                userEmail: response.data.email,
                                isAdmin: response.data.isAdmin
                            };
                            setAuthToken(freshUser);
                            localStorage.setItem('auth-user', JSON.stringify(freshUser));
                            console.log("✅ Session validée par le serveur");
                        }
                    } catch (validationError) {
                        // Le token est expiré ou invalide, l'intercepteur s'en chargera
                        console.warn("⚠️ Token potentiellement expiré, l'intercepteur va tenter un refresh");
                    }
                }
            } catch (error) {
                console.error("Erreur restauration session", error);
                logout();
            } finally {
                setLoading(false);
            }
        };

        loadUserFromStorage();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AuthContext.Provider value={{ authToken, login, logout, loading, refreshToken }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
